import { Link } from "react-router-dom";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const SUITS = ["♠", "♥", "♦", "♣"];

const SECTIONS = [
  {
    n: "01",
    title: "About Players Lounge",
    blocks: [
      {
        p: "Players Lounge is a private, invitation-only social gathering for men aged 18+. Attendance is subject to availability, suitability and compliance with these Terms.",
      },
    ],
  },
  {
    n: "02",
    title: "Eligibility & admission",
    blocks: [
      {
        h: "2.1 Age requirement",
        list: [
          "All attendees must be 18 years or older.",
          "We reserve the right to request photo ID from any attendee who appears under 25 years of age.",
          "Failure to provide valid ID on request may result in refusal of entry without a refund.",
        ],
      },
      {
        h: "2.2 Right to refuse entry",
        p: "We reserve the right to refuse entry or remove any attendee whose behaviour is unsafe, disruptive, non-consensual, intoxicated, drug-affected or otherwise inconsistent with the Players Lounge vibe and code of conduct.",
      },
    ],
  },
  {
    n: "03",
    title: "Tickets & payment",
    blocks: [
      {
        h: "3.1 Ticket platform",
        list: [
          "Tickets must be booked and paid for via Humanitix, our ticketing platform.",
          "We do not accept cash ticket payments.",
        ],
      },
      {
        h: "3.2 Invitation-only attendance",
        list: [
          "Ticket purchase or waitlist registration does not guarantee future invitations.",
          "Invitations and attendance may be limited to preserve capacity, safety and event culture.",
        ],
      },
    ],
  },
  {
    n: "04",
    title: "Venue & location details",
    blocks: [
      {
        list: [
          "Venue details are provided to ticketholders approximately 5–7 days before the event.",
          "Venue locations may vary between gatherings.",
          "Attendees must not disclose or publish the venue location to non-attendees.",
        ],
      },
    ],
  },
  {
    n: "05",
    title: "Arrival, lockout & attendance",
    blocks: [
      {
        h: "5.1 Arrival time",
        list: [
          "Please arrive on time.",
          "We operate a strict lockout policy: doors close 20 minutes after the event start time.",
          "Late arrival may result in refusal of entry without a refund.",
        ],
      },
      {
        h: "5.2 If you cannot attend or are running late",
        list: [
          "If you cannot attend, notify us as early as possible so we can offer your place to someone on the waitlist.",
          "If you are running late, contact us immediately (entry may still be refused after lockout).",
        ],
      },
    ],
  },
  {
    n: "06",
    title: "Conduct & consent",
    blocks: [
      {
        p: "All attendees are expected to contribute positively to the Players Lounge environment.",
      },
      {
        h: "6.1 Consent is mandatory",
        p: "Always ask. Never assume consent. Consent can be withdrawn at any time. Consent exists when those involved:",
        list: [
          "agree with each other to take part",
          "really want to take part — they don't feel they have to",
          "check in with each other to make sure each wants to take part",
          "show or say they want to take part in a clear and open way",
        ],
      },
      {
        h: "6.2 Participation expectations",
        list: [
          "Be an active participant in the event.",
          "If you are not playing in a play area, use the social/chill areas and avoid obstructing others.",
        ],
      },
      {
        h: "6.3 Respect for others",
        list: [
          "Do not pressure anyone for play, contact details or personal information.",
          "Do not interrogate other attendees about their personal life.",
          "Be respectful when declining or being declined.",
        ],
      },
      {
        h: "6.4 Space use",
        list: [
          "Keep play in designated play spaces.",
          "Keep conversations and socialising in designated social spaces.",
          "Do not interfere with or interrupt others unless expressly invited.",
        ],
      },
      {
        h: "6.5 Cleanliness",
        list: ["Dispose of rubbish in the bins provided.", "Clean up after yourself."],
      },
      {
        p: "Failure to follow the code of conduct may result in removal from the event and/or exclusion from future invitations. See also our House Rules.",
        link: { to: "/house-rules", label: "House Rules", before: "See also our ", after: "." },
      },
    ],
  },
  {
    n: "07",
    title: "Privacy, discretion & recording devices",
    blocks: [
      {
        h: "7.1 No recordings, ever",
        list: [
          "No recording devices are permitted in the Players Lounge.",
          "This includes phones, smart glasses, Meta glasses and similar devices.",
          "If a recording device is used in breach of this rule, we may require deletion of the footage, and we may ask you to leave and/or exclude you from future events.",
        ],
      },
      {
        h: "7.2 Discretion",
        p: "Attendees may discuss general aspects of the event with others, but must not identify the following without explicit consent:",
        list: ["the location of the gathering, or", "other attendees (including names, images or identifying details)"],
      },
      {
        h: "7.3 Contact information",
        p: "We do not share or forward attendees' contact details (including names, numbers and email addresses) to other attendees. For how we handle your information, see our Privacy note.",
        link: { to: "/privacy", label: "Privacy note", before: "For how we handle your information, see our ", after: "." },
      },
    ],
  },
  {
    n: "08",
    title: "Sexual health & personal responsibility",
    blocks: [
      {
        h: "8.1 Sexual health disclosure",
        p: "Players Lounge does not collect or verify attendees' STI/HIV status, testing history or prevention strategies.",
      },
      {
        h: "8.2 Personal responsibility",
        p: "All attendees are responsible for making their own informed decisions regarding sexual health, safer sex practices and risk mitigation.",
      },
      {
        h: "8.3 Medical and health advice",
        p: "Nothing provided by Players Lounge constitutes medical advice. Please consult your doctor or a sexual health clinic for guidance relevant to your circumstances.",
      },
    ],
  },
  {
    n: "09",
    title: "Alcohol, drugs & intoxication",
    blocks: [
      {
        h: "9.1 Intoxication",
        p: "Attendees are responsible for consuming alcohol responsibly. If you arrive intoxicated, or become alcohol-affected to the point that your behaviour impacts safety or the enjoyment of others, you may be refused entry or asked to leave.",
      },
      {
        h: "9.2 No drugs",
        list: [
          "The use of drugs at Players Lounge is not permitted.",
          "If you are drug-affected and it affects safety, behaviour or the event environment, you may be asked to leave and may not be invited back.",
        ],
      },
    ],
  },
  {
    n: "10",
    title: "Clothing, nudity & facilities",
    blocks: [
      {
        h: "10.1 Dress code",
        p: "Attendees may wear as much or as little as they choose, provided their presentation aligns with the gathering theme.",
      },
      {
        h: "10.2 Change facilities and belongings",
        list: [
          "A place to change is generally available.",
          "Attendees are encouraged to bring a bag for clothing and belongings.",
        ],
      },
      {
        h: "10.3 Shower facilities",
        list: [
          "A shower may be available, with body wash supplied.",
          "Towels are not supplied unless stated otherwise.",
          "Shower access is limited and attendees must be considerate of time and water use.",
        ],
      },
      {
        h: "10.4 Douching facilities",
        list: [
          "Due to limited facilities, attendees should prepare before arrival.",
          "We do not provide hoses or bulbs.",
        ],
      },
      {
        h: "10.5 Injectable ED medication (e.g. Trimix/Bimix)",
        list: [
          "Attendees using prescribed injectables for ED may use a bathroom discreetly.",
          "No sharps are to be disposed of in venue bins.",
          "Used syringes must be taken home and disposed of safely.",
        ],
      },
    ],
  },
  {
    n: "11",
    title: "Personal & lost property",
    blocks: [
      {
        h: "11.1 Personal property",
        list: [
          "Do not bring valuables with you.",
          "Players Lounge is not responsible for loss, theft or damage to personal property, except where required by law.",
        ],
      },
      {
        h: "11.2 Lost property",
        list: [
          "If you leave an item behind, email us with a description.",
          "Found items are held for 30 days, then may be donated or disposed of.",
          "We do not deliver lost items; collection must be arranged.",
        ],
      },
    ],
  },
  {
    n: "12",
    title: "Cancellations, refunds & no-shows",
    blocks: [
      {
        h: "12.1 Cancellation by attendee",
        p: "If you can no longer attend, notify us as soon as possible. Refunds are handled as follows:",
        table: [
          ["More than 7 days before", "Full refund"],
          ["Within 7 days of the event", "No refund"],
          ["No-show", "No refund, regardless of reason"],
        ],
      },
      {
        h: "12.2 Repeated no-shows",
        p: "Repeated no-shows (e.g. more than one within a six-month period) may result in removal from the mailing list or future invitation lists.",
      },
      {
        h: "12.3 Change of mind once the event is underway",
        p: "Once the gathering is underway, change-of-mind refunds are not provided.",
      },
      {
        h: "12.4 If Players Lounge cancels",
        p: "If we cancel an event, ticketholders will be notified and offered a refund or transfer (where applicable), subject to the ticket platform's processing rules.",
      },
    ],
  },
  {
    n: "13",
    title: "Removal from an event",
    blocks: [
      {
        p: "We may ask an attendee to leave immediately (without refund) for reasons including, but not limited to:",
        list: [
          "non-consensual conduct",
          "harassment, intimidation or aggressive behaviour",
          "drug use at the event",
          "intoxication affecting the safety or enjoyment of others",
          "breach of the privacy/recording rules",
          "breach of venue rules or host instructions",
        ],
      },
    ],
  },
  {
    n: "14",
    title: "Assumption of risk & liability",
    blocks: [
      {
        p: "Attendees acknowledge that participation in adult social environments carries personal, physical, emotional and social risks. You knowingly and freely assume all risks arising from your attendance and any participation in activities, whether those risks are known or unknown. By attending, you:",
        list: [
          "accept responsibility for your own choices, interactions and boundaries",
          "agree to act safely and respectfully",
          "understand that Players Lounge does not supervise private interactions between consenting adults",
        ],
      },
      {
        p: "To the maximum extent permitted by law, Players Lounge excludes liability for loss, injury, damage or expense arising from attendance, except where liability cannot be excluded under applicable law. Nothing in these Terms excludes rights you may have under the Australian Consumer Law.",
      },
    ],
  },
  {
    n: "15",
    title: "Mailing list & communications",
    blocks: [
      {
        list: [
          "If you are not receiving invitations, contact us from the email address you used to sign up.",
          "We may suspend or remove a person from the mailing list for repeated no-shows, conduct issues, not following the instructions of the host, or misuse of event information.",
        ],
      },
    ],
  },
  {
    n: "16",
    title: "Questions? Talk to the house.",
    blocks: [
      {
        p: "For questions, lost property or attendance issues, contact Players Lounge at hello@playersloungesyd.com.",
        email: true,
      },
    ],
  },
  {
    n: "17",
    title: "Governing law",
    blocks: [
      {
        p: "These Terms are governed by the laws of New South Wales, Australia.",
      },
    ],
  },
];

const Block = ({ block }) => (
  <div>
    {block.h && <h3 className="mt-2 font-serif text-lg italic text-brass">{block.h}</h3>}
    {block.p && !block.link && (
      <p className="mt-2 text-[15px] leading-relaxed text-ivory/70">
        {block.email ? (
          <>
            For questions, lost property or attendance issues, contact Players Lounge at{" "}
            <a href="mailto:hello@playersloungesyd.com" className="text-brass underline underline-offset-4 hover:text-ivory">
              hello@playersloungesyd.com
            </a>
            .
          </>
        ) : (
          block.p
        )}
      </p>
    )}
    {block.link && (
      <p className="mt-2 text-[15px] leading-relaxed text-ivory/70">
        {block.p.split(block.link.before)[0]}
        {block.link.before}
        <Link to={block.link.to} className="text-brass underline underline-offset-4 hover:text-ivory">
          {block.link.label}
        </Link>
        {block.link.after}
      </p>
    )}
    {block.list && (
      <ul className="mt-2 space-y-2">
        {block.list.map((item, i) => (
          <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-ivory/70">
            <span aria-hidden="true" className="mt-0.5 text-xs text-brass">♦</span>
            {item}
          </li>
        ))}
      </ul>
    )}
    {block.table && (
      <div className="mt-4 overflow-hidden rounded-xl border border-brass/20">
        {block.table.map(([k, v], i) => (
          <div
            key={k}
            className={`flex items-center justify-between gap-4 px-5 py-3.5 text-sm ${
              i % 2 === 0 ? "bg-ink/60" : "bg-charcoal/40"
            }`}
          >
            <span className="text-ivory/75">{k}</span>
            <span className={v.startsWith("Full") ? "font-semibold text-brass" : "text-ivory/60"}>{v}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Terms = () => (
  <div data-testid="terms-page">
    <section className="lamp-glow mx-auto max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pt-44">
      <Eyebrow suit="♠">The fine print</Eyebrow>
      <h1 className="mt-4 font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl">
        <MaskedLine delay={0.1}>Terms &amp; Conditions.</MaskedLine>
        <MaskedLine delay={0.25} className="italic text-brass">
          Read before you play.
        </MaskedLine>
      </h1>
      <FadeUp delay={0.4}>
        <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
          These Terms apply to all Players Lounge gatherings, ticket purchases and attendance. By
          purchasing a ticket, joining the waitlist or attending a Players Lounge event, you agree
          to these Terms.
        </p>
        <p className="mt-4 text-xs uppercase tracking-[0.25em] text-ivory/40">
          Last updated: 26 February 2026
        </p>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-4xl px-5 sm:px-8">
      <FadeUp>
        <nav
          aria-label="Contents"
          className="rounded-2xl border border-brass/15 bg-charcoal/50 p-7 sm:p-9"
        >
          <p className="text-[11px] uppercase tracking-[0.3em] text-brass">
            <span aria-hidden="true" className="mr-2">♦</span>Contents
          </p>
          <ol className="mt-5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
            {SECTIONS.map((s) => (
              <li key={s.n}>
                <a
                  href={`#section-${s.n}`}
                  data-testid={`terms-toc-${s.n}`}
                  className="group flex items-baseline gap-3 text-sm text-ivory/65 transition-colors hover:text-brass"
                >
                  <span className="font-serif text-xs italic text-brass/60">{s.n}</span>
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </FadeUp>

      <div className="space-y-6 py-14">
        {SECTIONS.map((s, i) => (
          <FadeUp key={s.n} delay={0.02}>
            <section
              id={`section-${s.n}`}
              data-testid={`terms-section-${s.n}`}
              className="scroll-mt-28 rounded-2xl border border-brass/15 bg-charcoal/50 p-7 sm:p-9"
            >
              <p className="text-[11px] uppercase tracking-[0.3em] text-brass">
                <span aria-hidden="true" className="mr-2">{SUITS[i % 4]}</span>Section {s.n}
              </p>
              <h2 className="mt-2 font-serif text-2xl text-ivory sm:text-3xl">{s.title}</h2>
              <div className="mt-4 space-y-4">
                {s.blocks.map((b, j) => (
                  <Block key={j} block={b} />
                ))}
              </div>
            </section>
          </FadeUp>
        ))}
      </div>

      <FadeUp>
        <div className="flex flex-wrap gap-4 pb-24">
          <Link
            to="/contact"
            data-testid="terms-contact-button"
            className="inline-flex h-12 items-center rounded-xl bg-burgundy px-7 text-sm font-semibold text-ivory transition-colors hover:bg-[#a33847]"
          >
            Email the Lounge
          </Link>
          <Link
            to="/house-rules"
            data-testid="terms-house-rules-link"
            className="inline-flex h-12 items-center rounded-xl border border-brass/30 px-7 text-sm font-semibold text-brass transition-colors hover:border-brass hover:bg-brass/10"
          >
            Read the House Rules
          </Link>
        </div>
      </FadeUp>
    </section>
  </div>
);

export default Terms;
